import { Button, Stack, Typography } from '@mui/material';
import { Frame } from '../../components/layouts/Frame';
import { DataTable } from '../../components/global/Table/DataTable';
import { Link } from '../../components/global/Link';
import { DataTableHeadCellData } from '../../components/global/Table/DataTableHeader';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useAPI } from '../../hooks/useAPI';
import { CorrectionData } from '../../types';
import moment from 'moment';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { AddCorrection } from './AddCorrection';
import { EditCorrection } from './EditCorrection';
import { EditIcon } from '../../assets/svgs/EditIcon';
import { TagIcon } from '../../assets/svgs/TagIcon';

const headCells: DataTableHeadCellData[] = [
  {
    id: 'id',
    disablePadding: true,
    label: 'ID',
    align: 'left',
    sortable: true,
    searchable: true,
    width: '200px',
  },
  {
    id: 'date',
    disablePadding: true,
    label: 'Date',
    align: 'left',
    sortable: true,
    searchable: true,
    width: '200px',
  },
  {
    id: 'job_id',
    disablePadding: true,
    label: 'Job Ref',
    align: 'left',
    cellAlign: 'left',
    sortable: true,
    searchable: true,
  },
  {
    id: 'information',
    disablePadding: true,
    label: 'Information',
    align: 'left',
    cellAlign: 'left',
    sortable: true,
    searchable: true,
  },
  {
    id: 'edit',
    disablePadding: false,
    label: 'Edit',
    width: '50px',
  },
];

interface CorrectionTableData {
  id: any;
  date: any;
  job_id: any;
  information: any;
  edit: JSX.Element;
}

export const Corrections = () => {
  const isLoading = useRef(false);
  const [sIsLoading, setSIsLoading] = useState(false);
  const { isAuthenticated } = useAuth();
  const { fetchData, token } = useAPI();
  const [rows, setRows] = useState<CorrectionTableData[]>([]);
  const [corrections, setCorrections] = useState<CorrectionData[]>([]);
  const navigate = useNavigate();

  const refetchCorections = async () => {
    setSIsLoading(true);
    isLoading.current = true;
    const { errors, data } = await fetchData('/corrections');
    setCorrections(data ?? []);
    isLoading.current = false;
    setSIsLoading(false);
  };

  const onAddCorrection = () => {
    navigate('/corrections/create');
  };

  const onBulkDelete = async (ids: number[]) => {
    await fetchData('/corrections/delete', { id: ids }, 'POST');
    refetchCorections();
  };

  const getRows = (corrections: CorrectionData[]) => {
    const result: CorrectionTableData[] = [];
    for (let i = 0; i < corrections.length; i++) {
      result.push({
        id: `${corrections[i].id}`,
        information: (
          <Typography>{JSON.stringify(corrections[i].information)}</Typography>
        ),
        job_id: <Typography>{corrections[i].job_id}</Typography>,
        date: (
          <Typography>
            {moment(corrections[i].created_at).format('MM/DD/YYYY')}
          </Typography>
        ),
        edit: (
          <Link to={`/corrections/${corrections[i].id}`}>
            <EditIcon />
          </Link>
        ),
      });
    }
    return result;
  };

  useEffect(() => {
    if (isAuthenticated && !isLoading.current) {
      refetchCorections();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    setRows(getRows(corrections));
  }, [corrections]);

  return (
    <Frame
      title="Corrections"
      toolbarButtons={
        <>
          <Button
            variant="contained"
            onClick={onAddCorrection}
            startIcon={<TagIcon color="#FFFFFF" />}>
            Add Correction
          </Button>
        </>
      }>
      <Stack>
        <Routes>
          <Route
            path="/create/"
            element={
              <AddCorrection refetchCorections={refetchCorections} />
            }></Route>
          <Route
            path="/:correction_id/"
            element={
              <EditCorrection refetchCorections={refetchCorections} />
            }></Route>
        </Routes>
        <DataTable
          title="Corrections"
          headCells={headCells}
          rows={rows}
          isLoading={sIsLoading}
          hideIdCheckbox={true}
          hideDeleteButton={true}
          onBulkDelete={onBulkDelete}
        />
      </Stack>
    </Frame>
  );
};
